import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { InputFieldComponent } from '../../../../ui/inputField/inputField.component';
import { CustomizableButtonComponent } from '../../../../ui/customizable-button/customizable-button.component';
import { UserProfileService } from '../../../../../services/user/user-profile.service';
import { SettingsService } from '../../../../../services/settings/settings.service';
import { ToastService } from '../../../../../services/toast/toast.service';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

const SPECIAL_CHAR = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/;

/** The rules the new password must meet, checked live as the person types. */
interface PasswordRule {
  label: string;
  met: boolean;
}

@Component({
  selector: 'app-change-password-form',
  standalone: true,
  imports: [CommonModule, InputFieldComponent, ReactiveFormsModule, CustomizableButtonComponent],
  templateUrl: './change-password-form.component.html',
  styleUrls: [
    '../../../../../pages/organization/settings/settings-ui.css',
    './change-password-form.component.css',
  ],
})
export class ChangePasswordFormComponent {
  constructor(private builder: FormBuilder) {}

  settingsService = inject(SettingsService);
  userProfileService = inject(UserProfileService);
  private toast = inject(ToastService);

  isUpdatingPassword: boolean = false;
  submitted: boolean = false;
  showCurrentPassword: boolean = false;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;

  // Feedback: success goes to a toast and clears itself; a failure stays in the panel.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;

  changePasswordForm = this.builder.group(
    {
      currentPassword: ['', Validators.required],
      newPassword: ['', [Validators.required, Validators.minLength(8), this.passwordStrengthValidator]],
      confirmPassword: ['', Validators.required],
    },
    {
      validators: this.passwordMatchValidator,
    }
  );

  get userProfileData() {
    return this.userProfileService.userDetails;
  }

  /** Live checklist under the new password. */
  get rules(): PasswordRule[] {
    const value = this.changePasswordForm.get('newPassword')?.value || '';
    return [
      { label: 'At least 8 characters', met: value.length >= 8 },
      { label: 'An uppercase letter', met: /[A-Z]/.test(value) },
      { label: 'A number', met: /[0-9]/.test(value) },
      { label: 'A symbol, like ! or #', met: SPECIAL_CHAR.test(value) },
    ];
  }

  get rulesMet(): number {
    return this.rules.filter((rule) => rule.met).length;
  }

  get currentPasswordErrorMsg() {
    const currentPassword = this.changePasswordForm.get('currentPassword');
    if (currentPassword?.errors?.['required'] && this.submitted) {
      return 'Enter the password you sign in with now.';
    }
    return '';
  }

  get newPasswordErrorMsg() {
    const newPassword = this.changePasswordForm.get('newPassword');
    if (!this.submitted) return '';
    if (newPassword?.errors?.['required']) {
      return 'Enter a new password.';
    } else if (newPassword?.errors?.['minlength'] || newPassword?.errors?.['passwordStrength']) {
      return 'The new password does not meet every rule below yet.';
    }
    return '';
  }

  get confirmPasswordErrorMsg() {
    const confirmPassword = this.changePasswordForm.get('confirmPassword');
    if (confirmPassword?.errors?.['required'] && this.submitted) {
      return 'Type the new password again.';
    } else if (
      this.changePasswordForm.errors?.['passwordMismatch'] &&
      (this.submitted || (confirmPassword?.dirty && !!confirmPassword?.value))
    ) {
      return 'This does not match the new password.';
    }
    return '';
  }

  // Custom validator for password strength
  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const password = control.value;

    if (!password) {
      return null;
    }

    const hasUpperCase = /[A-Z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = SPECIAL_CHAR.test(password);

    const passwordValid = hasUpperCase && hasNumber && hasSpecialChar;

    return passwordValid ? null : { passwordStrength: true };
  }

  // Custom validator for password matching
  passwordMatchValidator(control: AbstractControl): ValidationErrors | null {
    const newPassword = control.get('newPassword');
    const confirmPassword = control.get('confirmPassword');

    if (!newPassword || !confirmPassword) {
      return null;
    }

    return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
  }

  // Toggle password visibility
  toggleCurrentPasswordVisibility() {
    this.showCurrentPassword = !this.showCurrentPassword;
  }

  toggleNewPasswordVisibility() {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  handleSubmit() {
    this.submitted = true;
    this.showFeedback = false;

    if (this.changePasswordForm.invalid) {
      return;
    }

    const userId = this.userProfileData?.sub;
    if (!userId) {
      this.displayFeedback('We could not read your account. Sign out, sign in again and retry.', 'error');
      return;
    }

    const passwordData = {
      currentPassword: this.changePasswordForm.value.currentPassword!,
      newPassword: this.changePasswordForm.value.newPassword!,
    };

    this.isUpdatingPassword = true;

    this.settingsService.updateUserPassword(userId, passwordData).subscribe({
      next: () => {
        this.isUpdatingPassword = false;
        this.displayFeedback('Password changed. Use the new one next time you sign in.', 'success');
        // Reset form after successful password change
        this.changePasswordForm.reset();
        this.submitted = false;
        this.showCurrentPassword = false;
        this.showNewPassword = false;
        this.showConfirmPassword = false;
      },
      error: (error) => {
        this.isUpdatingPassword = false;
        this.displayFeedback(this.getPasswordErrorMessage(error), 'error');
      },
    });
  }

  // Build a user-friendly message from the API/HTTP error response
  private getPasswordErrorMessage(error: any): string {
    const body = error?.error;
    const status = error?.status;

    // RFC 7807 problem+json with field-level validation errors
    const fieldErrors = body?.fieldErrors;
    if (Array.isArray(fieldErrors) && fieldErrors.length) {
      const messages = fieldErrors
        .map((fe: any) => fe?.message)
        .filter((msg: any): msg is string => !!msg);
      if (messages.length) {
        return messages.join(' ');
      }
    }

    // Ignore machine-readable message codes (e.g. 'error.validation').
    const candidate = body?.error || body?.detail || body?.message || error?.message;
    const apiMessage = this.isUserFriendlyMessage(candidate) ? candidate : '';

    if (status === 400 || status === 401 || status === 403) {
      return apiMessage || 'The current password is not right. Check it and try again.';
    }

    if (status >= 500) {
      return 'Your password was not changed because of a problem on our side. Try again in a moment.';
    }

    if (status === 0) {
      return 'We could not reach the server. Check your connection and try again.';
    }

    return apiMessage || 'Your password was not changed. Try again.';
  }

  // Filters out machine-readable codes like 'error.validation'
  private isUserFriendlyMessage(message: unknown): message is string {
    return typeof message === 'string' && message.trim().length > 0 && message.includes(' ');
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    if (type === 'success') {
      this.showFeedback = false;
      this.toast.success(message);
    } else {
      this.showFeedback = true;
    }
  }
}
